/**
 * El `?ref=` del enlace de un referidor, guardado en el navegador hasta que hay
 * con quién asociarlo.
 *
 * ── POR QUÉ EL NAVEGADOR Y NO LA URL ───────────────────────────────────────
 *
 * Quien llega por el enlace de un referidor cae en una ruta protegida, y lo
 * primero que hace `AuthGuard` es mandarlo a /login y de ahí a auth.tbit.app. En
 * ese viaje la query se pierde: el OIDC devuelve al `redirect_uri` registrado,
 * no a la dirección con la que empezó. Cuando vuelve —ya con sesión— el `?ref`
 * hace rato que no existe.
 *
 * `localStorage` sí sobrevive: es del mismo origen (jobs.tbit.app) antes y
 * después del rodeo por auth. Por eso la captura pasa en `main.tsx` ANTES de
 * montar React, que es lo único garantizado a correr antes del redirect.
 *
 * ── EL PRIMER TOQUE GANA ───────────────────────────────────────────────────
 *
 * Si dos referidores mandan su enlace, el crédito es del primero. Sobrescribir
 * convertiría el programa en una carrera por ser el último clic, que es
 * exactamente lo que no se quiere premiar. El pendiente vencido sí se puede
 * reemplazar: a los 30 días ya no hay nada que proteger, y bloquear una captura
 * nueva con un código muerto sería peor que no haber guardado nada.
 *
 * Todo lo que toca `localStorage` va en try/catch: en modo privado (y en un
 * Safari con las cookies bloqueadas) el acceso TIRA, y un programa de referidos
 * no puede ser lo que impide que la app arranque.
 */

/** La llave. Con el prefijo del producto: este origen es sólo de job-apply. */
export const PENDING_REFERRAL_KEY = 'job_apply_pending_ref';

const URL_PARAM = 'ref';

/** El mismo que valida el backend. Mandarle basura es gastar una petición. */
const CODE_SHAPE = /^[A-Z0-9_-]{3,24}$/;

/** 30 días. Un enlace de hace dos meses ya no explica esta cuenta. */
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

interface PendingReferral {
  code: string;
  /** ISO — se lee en devtools sin traducir un epoch a mano. */
  captured_at: string;
}

/** Mayúsculas y sin espacios, o null si no tiene la forma que el backend acepta. */
export function normalizeReferralCode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const code = raw.trim().toUpperCase();
  return CODE_SHAPE.test(code) ? code : null;
}

function read(): PendingReferral | null {
  try {
    const raw = window.localStorage.getItem(PENDING_REFERRAL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingReferral> | null;
    const code = normalizeReferralCode(parsed?.code);
    if (!code) return null;
    return { code, captured_at: String(parsed?.captured_at ?? '') };
  } catch {
    // Sin storage, o con un valor de otra versión: no hay pendiente.
    return null;
  }
}

/**
 * Lee el `?ref=` y lo guarda. Silenciosa a propósito: no hay pantalla que
 * pueda reaccionar a esto —corre antes de que exista React— y un código
 * inválido no es un problema de quien abrió el enlace.
 */
export function captureReferralFromUrl(search: string = window.location.search): void {
  const code = normalizeReferralCode(new URLSearchParams(search).get(URL_PARAM));
  if (!code) return;

  // Sólo un pendiente VIVO bloquea; ver "el primer toque gana" arriba.
  if (getPendingReferral()) return;

  const entry: PendingReferral = { code, captured_at: new Date().toISOString() };
  try {
    window.localStorage.setItem(PENDING_REFERRAL_KEY, JSON.stringify(entry));
  } catch {
    // Modo privado: se pierde la atribución, no la visita.
  }
}

/** El código que todavía espera dueño, o null si no hay o si ya venció. */
export function getPendingReferral(): string | null {
  const entry = read();
  if (!entry) return null;

  const at = Date.parse(entry.captured_at);
  // Una fecha ilegible se trata como vencida: no se puede probar que sea reciente.
  if (Number.isNaN(at) || Date.now() - at > MAX_AGE_MS) return null;

  return entry.code;
}

export function clearPendingReferral(): void {
  try {
    window.localStorage.removeItem(PENDING_REFERRAL_KEY);
  } catch {
    // Si no se pudo leer tampoco se pudo escribir: no hay nada que borrar.
  }
}
